import type { ApiKeyAccessFormSubmitFn } from '../../form/ApiKeyAccessForm/ApiKeyAccessForm';
import type { UserLocalStorage } from '../../types/localStorage';

import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { twMerge } from 'tailwind-merge';
import useLocalStorageState from 'use-local-storage-state';

import { getEnv } from '../../lib/env';
import { getApiKey } from '../../lib/flotiq-client';
import { parseResponseErrors } from '../../lib/flotiq-client/response-errors';

import Button from '../../components/Button/Button';
import CancelButton from '../../components/Button/predefined/CancelButton/CancelButton';
import Loader from '../../components/Loader/Loader';

import { useUser } from '../../hooks/api';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';
import useDarkMode from '../../hooks/useDarkMode';
import useToken from '../../hooks/useToken';

import { ArrowRightCurvedIcon, FlotiqLogo } from '../../images/shapes';

import ApiKeyAccessForm from '../../form/ApiKeyAccessForm/ApiKeyAccessForm';

const testId = 'api-key-access';

/**
 * Where the keys may go: the redirect's own address when its hostname is in
 * `VITE_ALLOWED_API_KEY_REDIRECT_HOSTS` and it uses https, otherwise the same
 * address on localhost (a local CLI like flotiq-setup). The keys travel in the
 * query, so any other host could collect them.
 */
export const getApiKeyRedirectUrl = (redirectUri: string): URL | null => {
  let url: URL;
  try {
    url = new URL(redirectUri);
  } catch {
    return null;
  }

  const allowedHostnames = (getEnv('VITE_ALLOWED_API_KEY_REDIRECT_HOSTS') || '')
    .split(',')
    .map((hostname: string) => hostname.trim().toLowerCase())
    .filter(Boolean);

  const allowed =
    url.protocol === 'https:' &&
    allowedHostnames.includes(url.hostname.toLowerCase());
  if (!allowed) url.hostname = 'localhost';
  return url;
};

const ApiKeyAccess = () => {
  const { t } = useTranslation();
  const token = useToken();
  const navigate = useNavigate();
  const [user] = useLocalStorageState<UserLocalStorage>('cms.user');
  const [isLoading, setIsLoading] = useState(false);

  useDarkMode();

  const { state } = useLocation();
  const redirectUri = state?.redirect_uri;
  const responseType = state?.response_type;
  const keyType = state?.key_type;
  const applicationName = state?.application_name || 'Flotiq CLI';

  const roAccess = !keyType || keyType === 'both' || keyType === 'ro';
  const rwAccess = keyType === 'both' || keyType === 'rw';

  const {
    entity: userData,
    isLoading: userLoading,
    errors,
  } = useUser(user?.data?.id);

  useApiErrorsToast(errors);

  const urlToRedirect = useMemo(
    () => getApiKeyRedirectUrl(redirectUri),
    [redirectUri],
  );

  useEffect(() => {
    if (!urlToRedirect) {
      toast.error(t('ApiKeyAccess.WrongRedirect'));
      navigate('/');
    }
  }, [navigate, t, urlToRedirect]);

  const onSubmit = useCallback<ApiKeyAccessFormSubmitFn>(
    async ({ value }) => {
      if (!urlToRedirect) {
        navigate('/');
        return;
      }

      if (responseType !== 'code') {
        urlToRedirect.searchParams.append('status', 'rejected');
        window.location.replace(urlToRedirect.toString());
        return;
      }

      setIsLoading(true);

      try {
        const { body, status, ok } = await getApiKey(token, value.space, {
          global: true,
        });

        if (!ok) {
          throw parseResponseErrors(body, status);
        }

        const apiKeys = body.data;
        const roApiKey =
          apiKeys.find(
            ({ canCreate, canUpdate, canDelete, canRead }) =>
              !canCreate && !canUpdate && !canDelete && canRead,
          )?.apiKey || '';

        const rwApiKey =
          apiKeys.find(
            ({ canCreate, canUpdate, canDelete, canRead }) =>
              canCreate && canUpdate && canDelete && canRead,
          )?.apiKey || '';

        urlToRedirect.searchParams.append('status', 'approved');
        if (roAccess) {
          urlToRedirect.searchParams.append('api_key', roApiKey);
        }
        if (rwAccess) {
          urlToRedirect.searchParams.append('api_key_rw', rwApiKey);
        }
      } catch {
        urlToRedirect.searchParams.append('status', 'failed');
      }

      window.location.replace(urlToRedirect.toString());
    },
    [navigate, responseType, roAccess, rwAccess, token, urlToRedirect],
  );

  const onCancel = useCallback(() => {
    if (!urlToRedirect) {
      navigate('/');
      return;
    }
    urlToRedirect.searchParams.append('status', 'rejected');
    window.location.replace(urlToRedirect.toString());
  }, [navigate, urlToRedirect]);

  return (
    <div
      className="bg-slate-900 dark:bg-slate-700 min-h-screen
                flex items-center justify-center p-2 md:p-6"
    >
      <div
        className="relative bg-white flex flex-col max-w-4xl items-center justify-center
                  rounded-lg shadow-2xl p-6 md:py-10 lg:px-10 dark:bg-slate-900 dark:text-slate-200"
      >
        <FlotiqLogo className="h-10 mb-4 md:mb-6" />

        {isLoading && (
          <div
            className={twMerge(
              'absolute top-0 left-0 w-full h-full flex bg-white/80 dark:bg-slate-900/80',
              'justify-center items-center z-10 rounded-md',
            )}
          >
            <Loader type="spinner-grid" size="small" />
          </div>
        )}

        <h1 className="mb-2 md:mb-4 text-3xl xl:text-4xl">
          {t('ApiKeyAccess.AppAccess', { app: applicationName })}
        </h1>

        <h4 className="text-center leading-snug text-xl mb-4 md:mb-8">
          {t('ApiKeyAccess.AccessRequested')}
        </h4>

        {urlToRedirect && (
          <p className="text-center mb-4">
            {t('ApiKeyAccess.RedirectTarget')}{' '}
            <strong data-testid={`${testId}-redirect-host`}>
              {urlToRedirect.host}
            </strong>
          </p>
        )}

        <ul className="list-disc ml-4 md:ml-8 mb-4">
          {roAccess && (
            <li>
              <strong>{t('ApiKeyAccess.ReadOnlyApiKey')}</strong> -{' '}
              {t('ApiKeyAccess.ReadOnlyApiKeyDescription')}
            </li>
          )}
          {rwAccess && (
            <li>
              <strong>{t('ApiKeyAccess.ReadWriteApiKey')}</strong> -{' '}
              {t('ApiKeyAccess.ReadWriteApiKeyDescription')}
            </li>
          )}
        </ul>

        {userLoading ? (
          <div className="h-24 flex items-center">
            <Loader type="spinner-grid" size="small" />
          </div>
        ) : (
          <ApiKeyAccessForm
            onSubmit={onSubmit}
            disabled={isLoading}
            spaces={userData?.spaces}
            testId={testId}
          />
        )}

        <div className="flex flex-row items-center mt-4 md:mt-10 gap-6">
          <CancelButton onClick={onCancel} disabled={userLoading} />
          <Button
            type="submit"
            form="access-keys"
            disabled={userLoading}
            iconPosition="start"
            iconImage={<ArrowRightCurvedIcon className="w-3 min-w-3" />}
          >
            {t('ApiKeyAccess.GrantAccess')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ApiKeyAccess;
